import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    FormGroup,
    FormControlLabel,
    Switch,
    Box,
    Paper,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Button,
    TextareaAutosize,
    Stack,
    Grid
} from '@mui/material';
import { useAppStore } from '../../../../../store/appStore.tsx';
import { useValue } from '../../../../../context/ContextProvider';
import { updateConfigAlarma } from '../../../../../actions/alarmaActions';

// --- Función buildUint8FromBits (adaptada para 8 bits) ---
function buildUint8FromBits(bitConfig) {
    let resultUint8 = 0;
    for (const bitName in bitConfig) {
        if (Object.hasOwnProperty.call(bitConfig, bitName)) {
            const bitInfo = bitConfig[bitName];
            const position = bitInfo.position;
            const value = bitInfo.value;

            if (position < 0 || position > 7) { // Rango para Uint8 es 0-7
                //console.warn(`Advertencia: La posición del bit '${bitName}' (${position}) está fuera del rango válido para un uint8_t (0-7). Se ignorará.`);
                continue;
            }
            if (value === 1) {
                resultUint8 |= (1 << position);
            } else if (value === 0) {
                resultUint8 &= ~(1 << position);
            } else {
                //console.warn(`Advertencia: El valor para el bit '${bitName}' en posición ${position} debe ser 0 o 1. Se ignorará.`);
            }
        }
    }
    return resultUint8;
}
// --- Fin de buildUint8FromBits function ---

// Definición de los bits para el campo 'hab' (Uint8) de cada clave
const keyBitDefinitionsTemplate = {
    "bit0": { label: "Configurar", position: 0, value: 0 },
    "bit1": { label: "Armar Alarma", position: 1, value: 0 },
    "bit2": { label: "Desarmar Alarma", position: 2, value: 0 },
    "bit3": { label: "Bit 3", position: 3, value: 0 },
    "bit4": { label: "Bit 4", position: 4, value: 0 },
    "bit5": { label: "Bit 5", position: 5, value: 0 },
    "bit6": { label: "Bit 6", position: 6, value: 0 },
    "bit7": { label: "Bit 7", position: 7, value: 0 },
};

// Array de IDs para las 16 claves
const keyIds = Array.from({ length: 16 }, (_, i) => i); // [0, 1, ..., 15]

// Función auxiliar para inicializar el estado de todas las claves
const initializeKeysData = (initialJsonData = null) => {
    const initialState = {};
    keyIds.forEach((keyId) => {
        const keyConfig = {};
        const initialKeyData = initialJsonData?.find(item => item.id === keyId); // Buscar por ID
        const initialHabValue = initialKeyData?.hab;
        const initialClaveValue = initialKeyData?.clave ?? '---';

        for (const bitKey in keyBitDefinitionsTemplate) {
            let bitValue = 0;
            if (typeof initialHabValue === 'number') {
                bitValue = (initialHabValue >> keyBitDefinitionsTemplate[bitKey].position) & 1;
            }
            keyConfig[bitKey] = { ...keyBitDefinitionsTemplate[bitKey], value: bitValue };
        }
        initialState[keyId] = { // Usamos el ID como clave en el objeto de estado
            id: keyId,
            clave: initialClaveValue,
            bitConfig: keyConfig,
        };
    });
    return initialState;
};


function KeyConfigForm({ initialData, setInitialData }) {
    // Estado para todos los datos de las 16 claves
    const [allKeysData, setAllKeysData] = useState(() => initializeKeysData(initialData));
    // Estado para la clave actualmente seleccionada en el dropdown
    const [selectedKeyId, setSelectedKeyId] = useState(() => {
        // Asegurarse de que el ID inicial exista en las claves
        const initialId = initialData?.[0]?.id ?? 0;
        return keyIds.includes(initialId) ? initialId : keyIds[0];
    });

    // Estado para el Uint8 calculado de la clave seleccionada
    const [calculatedUint8, setCalculatedUint8] = useState(0);
    // Estado para el JSON que se muestra en el Textarea
    const [jsonOutput, setJsonOutput] = useState('');

    // Referencia para almacenar la configuración ORIGINAL (para el botón CANCELAR y dirty check)
    const originalConfigRef = useRef(null);

    // Estado para controlar si el formulario tiene cambios no guardados
    const [formDirty, setFormDirty] = useState(false);
    // Estado para controlar si el JSON mostrado está actualizado con los datos del formulario
    const [jsonNeedsUpdate, setJsonNeedsUpdate] = useState(true); // Inicialmente true para forzar un "Generar JSON"

    // Estado de las comunicaciones con el servidor/dispositivo
    const [estadoEnvioDev,  setEstadoEnvioDev] = useState({configId: 0, estado: "Inicial", color: "black" })

    // Valores guardados en nuestro appStore y/o context
    const { currentUser, device, startLoading, endLoading } = useAppStore(appStore => appStore);
    const { dispatch } = useValue();

    // --- useEffect para inicializar originalConfigRef y setInitialData al montar ---
    useEffect(() => {
        // Generar el JSON inicial completo para establecer la referencia original
        const initialFullConfig = generateFullConfigArray(allKeysData);
        originalConfigRef.current = initialFullConfig;
        setJsonOutput(JSON.stringify(initialFullConfig, null, 2)); // Mostrar el JSON inicial
        setJsonNeedsUpdate(false); // Al inicio, el JSON mostrado está actualizado
        setFormDirty(false); // Al inicio, el formulario no está dirty

        // Sincronizar con el padre si la prop setInitialData está presente
        if (setInitialData) {
            setInitialData(initialFullConfig); // Para el padre, solo enviamos el array de claves
        }
    }, []); // Se ejecuta solo una vez al montar

    // --- useEffect para calcular Uint8 de la clave seleccionada ---
    useEffect(() => {
        const currentKeyData = allKeysData[selectedKeyId];
        if (currentKeyData && currentKeyData.bitConfig) {
            const uint8 = buildUint8FromBits(currentKeyData.bitConfig);
            setCalculatedUint8(uint8);
        }
    }, [allKeysData, selectedKeyId]);

    // --- useEffect para determinar si el formulario está "dirty" y si el JSON necesita actualización ---
    useEffect(() => {
        const currentFullConfig = generateFullConfigArray(allKeysData);
        const currentFullConfigString = JSON.stringify(currentFullConfig, null, 2);
        const originalConfigString = JSON.stringify(originalConfigRef.current, null, 2);

        // Actualizar el estado 'formDirty'
        setFormDirty(currentFullConfigString !== originalConfigString);

        // Actualizar el estado 'jsonNeedsUpdate'
        setJsonNeedsUpdate(jsonOutput === '' || (currentFullConfigString !== jsonOutput));

        // Sincronizar con el padre
        if (setInitialData) {
            setInitialData(currentFullConfig);
        }
    }, [allKeysData, jsonOutput, setInitialData]);


    // --- Función auxiliar para generar el array de configuración completo ---
    const generateFullConfigArray = (currentAllKeysData) => {
        // Ordenar las claves por ID para asegurar un array consistente
        const sortedKeys = Object.values(currentAllKeysData).sort((a, b) => a.id - b.id);

        return sortedKeys.map((keyData) => {
            const habValue = buildUint8FromBits(keyData.bitConfig);
            return {
                id: keyData.id,
                clave: keyData.clave,
                hab: habValue,
            };
        });
    };

    // --- Manejadores de cambios ---
    const handleKeyChange = (event) => {
        setSelectedKeyId(Number(event.target.value)); // Convertir a número
    };

    const handleClaveChange = (event) => {
        const value = event.target.value;
        // Validar que solo sean números del 0 al 9
        if (/^[0-9]*$/.test(value) && value.length <= 6) { // Máximo 6 caracteres
            setAllKeysData(prevAllConfigs => ({
                ...prevAllConfigs,
                [selectedKeyId]: {
                    ...prevAllConfigs[selectedKeyId],
                    clave: value,
                },
            }));
        }
    };


    const handleBitChange = (bitName) => (event) => {
        setAllKeysData(prevAllConfigs => {
            const currentKeyConfig = { ...prevAllConfigs[selectedKeyId] };
            const updatedBitConfig = { ...currentKeyConfig.bitConfig };

            updatedBitConfig[bitName] = {
                ...updatedBitConfig[bitName],
                value: event.target.checked ? 1 : 0,
            };

            currentKeyConfig.bitConfig = updatedBitConfig;

            return {
                ...prevAllConfigs,
                [selectedKeyId]: currentKeyConfig,
            };
        });
    };

    // --- Funciones de los botones ---
    const handleGenerateJson = () => {
        const currentFullConfig = generateFullConfigArray(allKeysData);
        setJsonOutput(JSON.stringify(currentFullConfig, null, 2));
        setJsonNeedsUpdate(false); // El JSON mostrado está ahora actualizado
    };

    const sendConfig = async () => {
        const configToSend = generateFullConfigArray(allKeysData);

        //console.log("--- Enviando Configuración de Claves JSON ---");
        //console.log(configToSend);
        alert("Configuración de claves enviada (simulada). Revisa la consola.");


        startLoading();

        // updateResult, si todo sale bien, deberia contener el insertId, que es el configId
        setEstadoEnvioDev({configId: "---", estado: "Esperando respuesta del servidor/dispositivo", color: "black"})
        const updateResult = await updateConfigAlarma (currentUser, device.devId, "CMD_SET_CONFIG_CLAVES", configToSend, dispatch); 
        //console.log("updateResult.insertId: ", updateResult?.insertId); 
        if(updateResult?.resp === 0) {
            setEstadoEnvioDev({configId: updateResult.insertId, estado: "Última configuración actualizada con éxito.", color: "green"})
            // Si el envío es exitoso, actualizamos la configuración original
            originalConfigRef.current = configToSend;
            setFormDirty(false); // Ya no hay cambios pendientes
            setJsonNeedsUpdate(false); // El JSON mostrado (si se generó antes de enviar) es el que se envió
            setJsonOutput(JSON.stringify(configToSend, null, 2)); // Asegurarse de que el JSON mostrado sea el enviado
        } else {
            setEstadoEnvioDev({configId: 0, estado: "Error. Última configuración NO actualizada.", color: "red"})
        }
        
        endLoading();





        
    };

    const handleCancel = () => {
        if (formDirty) {
            const confirmCancel = window.confirm(
                "¿Estás seguro de que quieres cancelar? Todos los cambios no guardados se perderán."
            );
            if (confirmCancel) {
                // Restaurar la configuración a los valores originales
                const restoredKeysData = initializeKeysData(originalConfigRef.current);

                setAllKeysData(restoredKeysData);
                // Asegurarse de que selectedKeyId sea válido después de la restauración
                const initialIdAfterRestore = originalConfigRef.current?.[0]?.id ?? 0;
                setSelectedKeyId(keyIds.includes(initialIdAfterRestore) ? initialIdAfterRestore : keyIds[0]);

                setJsonOutput('');
                setJsonNeedsUpdate(true);
                setFormDirty(false);

                alert("Cambios descartados. Formulario restaurado a los valores originales.");
            }
        } else {
            alert("No hay cambios que descartar.");
        }
    };

    // Datos de la clave actualmente seleccionada para renderizar
    const currentKeyData = allKeysData[selectedKeyId];
    const currentKeyBitConfig = currentKeyData ? currentKeyData.bitConfig : {};
    const currentKeyClave = currentKeyData ? currentKeyData.clave : '';

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>
                    Configuración de Claves
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>

                    {/* Fila superior izquierda: Selección de Clave y Campos */}
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 12,
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'background.paper',
                                    px: 1,
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Claves
                            </Typography>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel id="key-select-label">Seleccionar Clave</InputLabel>
                                <Select
                                    labelId="key-select-label"
                                    id="key-select"
                                    value={selectedKeyId}
                                    label="Seleccionar Clave"
                                    onChange={handleKeyChange}
                                >
                                    {keyIds.map((id) => (
                                        <MenuItem key={id} value={id}>
                                            Clave {id}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Typography variant="subtitle1" component="p" gutterBottom>
                                Datos para Clave {selectedKeyId}:
                            </Typography>

                            <TextField
                                label="Contraseña (4-6 dígitos numéricos)"
                                type="text" // Cambiado a text para permitir validación más flexible
                                value={currentKeyClave}
                                onChange={handleClaveChange}
                                fullWidth
                                margin="normal"
                                inputProps={{
                                    minLength: 4,
                                    maxLength: 6,
                                    pattern: "[0-9]*", // Para teclado numérico en móviles
                                    inputMode: "numeric" // Para teclado numérico en móviles
                                }}
                                helperText="Solo números (0-9). Mínimo 4, máximo 6 caracteres."
                            />

                            <FormGroup sx={{ mt: 2 }}>
                                {currentKeyBitConfig &&
                                    Object.keys(currentKeyBitConfig).map((bitName) => (
                                        <FormControlLabel
                                            key={bitName}
                                            control={
                                                <Switch
                                                    checked={currentKeyBitConfig[bitName].value === 1}
                                                    onChange={handleBitChange(bitName)}
                                                    name={bitName}
                                                    color="primary"
                                                />
                                            }
                                            label={`${currentKeyBitConfig[bitName].label} (Bit ${currentKeyBitConfig[bitName].position})`}
                                        />
                                    ))}
                            </FormGroup>
                        </Paper>
                    </Grid>

                    {/* Fila superior derecha: JSON generado */}
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', pt: 4 }}>
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 12,
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'background.paper',
                                    px: 1,
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                }}
                            >
                                JSON de Configuración
                            </Typography>
                            {jsonOutput ? (
                                <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                                    <TextareaAutosize
                                        value={jsonOutput}
                                        minRows={10}
                                        maxRows={20}
                                        style={{
                                            width: '100%',
                                            border: `1px solid ${jsonNeedsUpdate ? '#ff9800' : '#ccc'}`,
                                            borderRadius: '4px',
                                            padding: '8px',
                                            fontFamily: 'monospace',
                                            resize: 'vertical',
                                            backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : 'white',
                                        }}
                                        readOnly
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography color="text.secondary">
                                        Genera el JSON para previsualizarlo aquí.
                                    </Typography>
                                </Box>
                            )}
                            <Button
                                variant="contained"
                                color={jsonNeedsUpdate ? "warning" : "primary"}
                                onClick={handleGenerateJson}
                                sx={{ mt: 'auto' }}
                                startIcon={jsonNeedsUpdate ? <i className="fa-solid fa-triangle-exclamation"></i> : null}
                            >
                                {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                            </Button>
                        </Paper>
                    </Grid>

                    {/* Fila inferior: Resumen de Valores de la Clave Actual */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 12,
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'background.paper',
                                    px: 1,
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Resumen de Valores (Clave {selectedKeyId})
                            </Typography>
                            <Box>
                                <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5 }}>
                                    **ID de Clave:** <strong>{selectedKeyId}</strong>
                                </Typography>
                                <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5 }}>
                                    **Contraseña:** <strong>{currentKeyClave || "No especificada"}</strong>
                                </Typography>
                                <Typography variant="body2" component="p" sx={{ ml: 2, mt: 1, fontFamily: 'monospace', mb: 0.5 }}>
                                    **Bits (Uint8):**
                                </Typography>
                                <Typography variant="body2" component="p" sx={{ ml: 4, fontFamily: 'monospace', mb: 0.5 }}>
                                    Decimal: <strong>{calculatedUint8}</strong>
                                </Typography>
                                <Typography variant="body2" component="p" sx={{ ml: 4, fontFamily: 'monospace', mb: 0.5 }}>
                                    Binario: <strong>{calculatedUint8.toString(2).padStart(8, '0')}</strong>
                                </Typography>
                                <Typography variant="body2" component="p" sx={{ ml: 4, fontFamily: 'monospace' }}>
                                    Hexadecimal: <strong>0x{calculatedUint8.toString(16).padStart(2, '0').toUpperCase()}</strong>
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Fila inferior: Resultados del envio de la configuracion al dispositivo */}
                    <Grid item xs={12} md={12}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 12,
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'background.paper',
                                    px: 1,
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Resultado envío al Dispositivo
                            </Typography>

                            <Box>
                                <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5}}>
                                    ConfigId: <strong>{estadoEnvioDev.configId}</strong>
                                </Typography>
                                <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5, color: estadoEnvioDev.color }}>
                                    Estado: <strong>{estadoEnvioDev.estado}</strong>
                                </Typography>
                            </Box>

                        </Paper>
                    </Grid>


                    {/* Fila de Botones de acción general (CANCELAR, ENVIAR) */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleCancel}
                                    startIcon={<i className="fa-solid fa-xmark"></i>}
                                    disabled={!formDirty}
                                >
                                    CANCELAR
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={sendConfig}
                                    startIcon={<i className="fa-solid fa-paper-plane"></i>}
                                    disabled={jsonNeedsUpdate || !formDirty}
                                >
                                    ENVIAR
                                </Button>
                            </Stack>
                        </Box>
                    </Grid>

                </Grid>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                    * Configuración de claves para el dispositivo. Cada clave tiene sus propios permisos.
                </Typography>
            </Paper>
        </Container>
    );
}

export default KeyConfigForm;